"use client";

import {
  addDays,
  addMonths,
  addWeeks,
  endOfMonth,
  endOfWeek,
  format,
  isSameMonth,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import { ChevronLeftIcon, ChevronRightIcon, PlusIcon } from "lucide-react";
import * as React from "react";

import { cn } from "../../cn";
import { Button } from "../button";
import { FOCUS_RING } from "../focus-ring";
import { HOVER_EASE } from "../states";
import { Segmented } from "../view-tabs";
import { parseDay, type StatusTone, TONE_DOT } from "./task-meta";

// The calendar view (Work OS container-view CalendarView): a toolbar (title, Today, previous /
// next, Month · Week) over a seven-column grid starting Monday. Items are chips (status dot +
// title) on their due day; dragging a chip to another day reports `onItemMove`. Out-of-month
// and weekend cells sit on --sunken; today's number is a filled --primary circle. It is a real
// `table` (weekday column headers, one cell per day) so readers can move by row and column.
// T4-VIEWS-MEDIA: a chip with a cover `thumb` or card `fields` becomes a small hairline card
// (thumbnail, status dot and title, then up to two fields on a muted line).

export type CalendarItem = {
  id: string;
  title: string;
  /** YYYY-MM-DD. */
  date: string;
  tone?: StatusTone;
  done?: boolean;
  /**
   * CALENDAR-C1: context from elsewhere (a calendar event on the board's calendar): a muted
   * chip that cannot be dragged; a click still reports `onItemOpen`.
   */
  readOnly?: boolean;
  /** A read-only item's time ("14:00"), shown before its title. */
  time?: string;
  /** T4-VIEWS-MEDIA: a cover thumbnail URL, shown as a small square before the title. */
  thumb?: string;
  /** T4-VIEWS-MEDIA: up to two short field values, on a muted line under the title. */
  fields?: string[];
};

export type CalendarViewProps = Omit<React.ComponentProps<"div">, "children"> & {
  items: CalendarItem[];
  /** Any YYYY-MM-DD inside the period shown. */
  anchor: string;
  /** YYYY-MM-DD; the caller's clock. */
  today?: string;
  mode?: "month" | "week";
  onModeChange?: (mode: "month" | "week") => void;
  /** Called with the new anchor (YYYY-MM-DD) for Previous, Next and Today. */
  onNavigate?: (anchor: string) => void;
  onItemOpen?: (id: string) => void;
  onItemMove?: (id: string, date: string) => void;
  onDayAdd?: (date: string) => void;
  /** Extra toolbar content on the right (e.g. an "Unscheduled · 4" button). */
  actions?: React.ReactNode;
  /** Items shown per month cell before "+n more". */
  maxPerDay?: number;
};

const ymd = (d: Date) => format(d, "yyyy-MM-dd");

export function CalendarView({
  items,
  anchor,
  today,
  mode = "month",
  onModeChange,
  onNavigate,
  onItemOpen,
  onItemMove,
  onDayAdd,
  actions,
  maxPerDay = 3,
  className,
  "aria-label": ariaLabel,
  ...props
}: CalendarViewProps) {
  const anchorDate = parseDay(anchor);
  const [dragId, setDragId] = React.useState<string | null>(null);
  const [dropDay, setDropDay] = React.useState<string | null>(null);
  const [expanded, setExpanded] = React.useState<string | null>(null);

  const first =
    mode === "month"
      ? startOfWeek(startOfMonth(anchorDate), { weekStartsOn: 1 })
      : startOfWeek(anchorDate, { weekStartsOn: 1 });
  const last =
    mode === "month"
      ? endOfWeek(endOfMonth(anchorDate), { weekStartsOn: 1 })
      : endOfWeek(anchorDate, { weekStartsOn: 1 });
  const days: Date[] = [];
  for (let d = first; d <= last; d = addDays(d, 1)) days.push(d);
  const weeks: Date[][] = [];
  for (let i = 0; i < days.length; i += 7) weeks.push(days.slice(i, i + 7));

  const byDay = new Map<string, CalendarItem[]>();
  for (const item of items) {
    const key = item.date.slice(0, 10);
    byDay.set(key, [...(byDay.get(key) ?? []), item]);
  }

  const title =
    mode === "month"
      ? format(anchorDate, "MMMM yyyy")
      : `${format(first, "d MMM")} – ${format(last, "d MMM yyyy")}`;
  const step = (n: number) =>
    onNavigate?.(ymd(mode === "month" ? addMonths(anchorDate, n) : addWeeks(anchorDate, n)));

  return (
    <div
      data-slot="calendar-view"
      data-mode={mode}
      className={cn("flex min-w-0 flex-col gap-3", className)}
      {...props}
    >
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="min-w-40 text-md font-semibold tracking-heading text-foreground">{title}</h2>
        {onNavigate ? (
          <>
            <Button variant="secondary" size="sm" onClick={() => today && onNavigate(today)}>
              Today
            </Button>
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label={mode === "month" ? "Previous month" : "Previous week"}
              onClick={() => step(-1)}
            >
              <ChevronLeftIcon />
            </Button>
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label={mode === "month" ? "Next month" : "Next week"}
              onClick={() => step(1)}
            >
              <ChevronRightIcon />
            </Button>
          </>
        ) : null}
        {onModeChange ? (
          <Segmented
            aria-label="Calendar range"
            size="sm"
            value={mode}
            onValueChange={(v) => onModeChange(v as "month" | "week")}
            options={[
              { value: "month", label: "Month" },
              { value: "week", label: "Week" },
            ]}
          />
        ) : null}
        <span className="ml-auto flex items-center gap-2">{actions}</span>
      </div>
      <div className="relative overflow-x-auto rounded-lg border border-border bg-border-subtle">
        <table
          aria-label={ariaLabel ?? title}
          className="w-full min-w-[640px] table-fixed border-separate border-spacing-px"
        >
          <thead>
            <tr>
              {days.slice(0, 7).map((d) => (
                <th
                  key={ymd(d)}
                  scope="col"
                  abbr={format(d, "EEEE")}
                  className="h-8 bg-sunken px-2 text-left text-xs font-medium text-muted-foreground"
                >
                  {format(d, "EEE")}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {weeks.map((week) => (
              <tr key={ymd(week[0] as Date)}>
                {week.map((d) => {
                  const key = ymd(d);
                  const list = byDay.get(key) ?? [];
                  const outside = mode === "month" && !isSameMonth(d, anchorDate);
                  const weekend = d.getDay() === 0 || d.getDay() === 6;
                  const isToday = key === today;
                  const shown =
                    mode === "week" || expanded === key ? list : list.slice(0, maxPerDay);
                  const more = list.length - shown.length;
                  return (
                    <td
                      key={key}
                      data-day={key}
                      data-drop={dropDay === key || undefined}
                      aria-current={isToday ? "date" : undefined}
                      onDragOver={(e) => {
                        if (!dragId) return;
                        e.preventDefault();
                        if (dropDay !== key) setDropDay(key);
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        const id = dragId ?? e.dataTransfer.getData("text/plain");
                        setDragId(null);
                        setDropDay(null);
                        const item = items.find((i) => i.id === id);
                        if (item && !item.readOnly && item.date.slice(0, 10) !== key)
                          onItemMove?.(id, key);
                      }}
                      className={cn(
                        "group/day p-1.5 align-top data-drop:outline-2 data-drop:-outline-offset-2 data-drop:outline-ring data-drop:outline-solid",
                        mode === "month" ? "h-24" : "h-72",
                        outside || weekend ? "bg-sunken" : "bg-card",
                      )}
                    >
                      <div className="mb-1 flex items-center justify-between">
                        <span
                          className={cn(
                            "inline-flex h-6 min-w-6 items-center justify-center rounded-full px-1 text-xs tabular-nums",
                            isToday
                              ? "bg-primary font-semibold text-primary-foreground"
                              : outside
                                ? "text-muted-foreground"
                                : "text-secondary",
                          )}
                        >
                          <span className="sr-only">{format(d, "EEEE d MMMM")}</span>
                          <span aria-hidden={true}>{format(d, "d")}</span>
                        </span>
                        {onDayAdd ? (
                          <button
                            type="button"
                            aria-label={`Add on ${format(d, "d MMMM")}`}
                            onClick={() => onDayAdd(key)}
                            className={cn(
                              "flex size-6 items-center justify-center rounded-sm text-muted-foreground opacity-0 group-hover/day:opacity-100 hover:bg-muted hover:text-foreground focus-visible:opacity-100 pointer-coarse:opacity-100",
                              FOCUS_RING,
                            )}
                          >
                            <PlusIcon aria-hidden={true} className="size-3.5" />
                          </button>
                        ) : null}
                      </div>
                      <ul className="m-0 flex list-none flex-col gap-0.5 p-0">
                        {shown.map((item) => (
                          <li key={item.id}>
                            {item.readOnly ? (
                              <button
                                type="button"
                                data-slot="calendar-view-item"
                                data-readonly=""
                                draggable={false}
                                onClick={() => onItemOpen?.(item.id)}
                                className={cn(
                                  "flex min-h-6 w-full min-w-0 items-start gap-1.5 rounded-sm border border-subtle bg-sunken px-1.5 py-0.5 text-left text-xs leading-snug text-muted-foreground hover:bg-muted",
                                  HOVER_EASE,
                                  FOCUS_RING,
                                )}
                                title={item.title}
                              >
                                {item.time ? (
                                  <span className="shrink-0 tabular-nums">{item.time}</span>
                                ) : null}
                                <span
                                  data-slot="calendar-view-title"
                                  className="line-clamp-2 min-w-0 flex-1 break-words"
                                >
                                  {item.title}
                                </span>
                              </button>
                            ) : (
                              <button
                                type="button"
                                data-slot="calendar-view-item"
                                draggable={Boolean(onItemMove)}
                                onDragStart={(e) => {
                                  e.dataTransfer.effectAllowed = "move";
                                  e.dataTransfer.setData("text/plain", item.id);
                                  setDragId(item.id);
                                }}
                                onDragEnd={() => {
                                  setDragId(null);
                                  setDropDay(null);
                                }}
                                onClick={() => onItemOpen?.(item.id)}
                                data-rich={item.thumb || item.fields?.length ? "" : undefined}
                                title={item.title}
                                className={cn(
                                  "flex min-h-6 w-full min-w-0 items-start gap-1.5 rounded-sm px-1.5 py-0.5 text-left text-xs leading-snug text-foreground hover:bg-muted",
                                  (item.thumb || item.fields?.length) &&
                                    "border border-subtle bg-card py-1",
                                  item.done && "text-muted-foreground",
                                  dragId === item.id && "opacity-40",
                                  HOVER_EASE,
                                  FOCUS_RING,
                                )}
                              >
                                {item.thumb ? (
                                  <img
                                    src={item.thumb}
                                    alt=""
                                    draggable={false}
                                    data-slot="calendar-view-thumb"
                                    className="size-7 shrink-0 rounded-xs bg-sunken object-cover"
                                  />
                                ) : null}
                                <span className="flex min-w-0 flex-1 flex-col">
                                  <span className="flex min-w-0 items-start gap-1.5">
                                    <span
                                      aria-hidden={true}
                                      className={cn(
                                        "mt-[0.4em] size-1.5 shrink-0 rounded-full",
                                        TONE_DOT[item.tone ?? "neutral"],
                                      )}
                                    />
                                    {/* Wave 20: the title takes the chip's full width and wraps
                                        to two lines before it is cut. */}
                                    <span
                                      data-slot="calendar-view-title"
                                      className={cn(
                                        "line-clamp-2 min-w-0 flex-1 break-words",
                                        item.done && "line-through",
                                      )}
                                    >
                                      {item.title}
                                    </span>
                                  </span>
                                  {item.fields?.length ? (
                                    <span
                                      data-slot="calendar-view-fields"
                                      className="truncate text-2xs text-muted-foreground"
                                    >
                                      {item.fields.slice(0, 2).join(" · ")}
                                    </span>
                                  ) : null}
                                </span>
                              </button>
                            )}
                          </li>
                        ))}
                      </ul>
                      {more > 0 ? (
                        <button
                          type="button"
                          onClick={() => setExpanded(key)}
                          className={cn(
                            "mt-0.5 h-6 rounded-sm px-1.5 text-xs text-muted-foreground hover:bg-muted hover:text-foreground",
                            FOCUS_RING,
                          )}
                        >
                          +{more} more
                        </button>
                      ) : null}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
